import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { TasksService } from '../../shared/services/tasks/tasks.service';
import { constants } from '../../shared/utils/constants';
import { LoggerService } from '../../shared/services/logger.service';
import { InsuranceCompany, OnlineTaskInspection } from './find-task-loose.interface';
import { ActivatedRoute, Router } from '@angular/router';
import {
  createLooseOnlineTaskInspectionMutation,
  listInsuranceCompanyQuery,
} from './find-task-loose.data';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { PartnersService } from '../../shared/services/partners/partners.service';
import { recaptchaStoreKey, recaptchaClearKey } from '../../shared/utils';
import { RecaptchaComponent } from 'ng-recaptcha';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-find-task-loose',
  templateUrl: './find-task-loose.component.html',
  styleUrls: ['./find-task-loose.component.scss'],
})
export class FindTaskLooseComponent implements OnInit, OnDestroy {
  @Input()
  task!: OnlineTaskInspection;
  @ViewChild('reCaptchaElement')
  reCaptchaElement!: RecaptchaComponent;
  insuranceCompanies: Array<InsuranceCompany> = [];
  recaptchaSiteKey = environment.recaptchaSiteKey;
  isServerError = false;
  loading = false;
  onlineInspectionFailed = false;
  onlineTaskInspectionInvalidInput = false;
  onlineTaskInspectionInvalidRecaptcha = false;
  constants = constants;
  subscriptions: any[] = [];

  public findTaskForm: FormGroup = this.fb.group({
    partnerId: [
      { value: null, disabled: false },
      Validators.compose([Validators.required]),
    ],
    claimNumber: [
      { value: '', disabled: false },
      Validators.compose([
        Validators.required,
        Validators.maxLength(35),
        Validators.minLength(5),
      ]),
    ],
    recaptchaKey: [
      { value: null, disabled: false },
      Validators.compose([Validators.required]),
    ],
  });

  partnerIdPrefilled: boolean = false;
  claimNumberPrefilled: boolean = false;

  constructor(
    public fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private logger: LoggerService,
    private tasksService: TasksService,
    private partnersService: PartnersService
  ) {}

  ngOnInit(): void {
    this.resolveUrlParameters();

    this.subscriptions.push(
      this.partnersService
        .getOnlineTaskInspectionPartners(listInsuranceCompanyQuery)
        .subscribe(
          (data) => {
            this.insuranceCompanies = data;

          },
          (error) => {
            this.logger.error('Loading enumeration failed', error);
          }
        )
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => {
      subscription.unsubscribe();
    });
  }

  async findTask() {
    this.onlineInspectionFailed = false;
    this.onlineTaskInspectionInvalidInput = false;
    this.onlineTaskInspectionInvalidRecaptcha = false;

    if (this.findTaskForm.status !== 'VALID') {
      if (
        this.findTaskForm.controls.claimNumber.status === 'VALID' &&
        this.findTaskForm.controls.partnerId.status === 'VALID'
      ) {
        this.onlineTaskInspectionInvalidRecaptcha = true;
      } else {
        this.onlineTaskInspectionInvalidInput = true;
      }

      this.reCaptchaElement.reset();
      this.loading = false;
      return;
    }

    this.loading = true;

    recaptchaStoreKey(this.findTaskForm.value.recaptchaKey);
    const claimNumber = this.findTaskForm.value.claimNumber;
    const partnerId = this.findTaskForm.value.partnerId;

    const task = await this.tasksService.createLooseOnlineInspection(
      partnerId,
      claimNumber,
      createLooseOnlineTaskInspectionMutation
    );

    recaptchaClearKey();
    if (!task.tokenId || !task.onlineTaskInspectionUrl) {
      this.reCaptchaElement.reset();
      this.onlineInspectionFailed = true;
      this.loading = false;
      return;
    }

    this.router.navigate(['/online-task-inspection', task.tokenId], {
      queryParams: {
        returnUrl: new URL(this.router.url, window.location.origin).pathname
      }
    });
  }

  private resolveUrlParameters(): void {
    this.route.queryParams.subscribe(params => {
      const partnerId = params['partnerId'] ?? null;
      const claimNumber = params['claimNumber'] ?? null;

      if (partnerId !== null) {
        this.findTaskForm.controls['partnerId'].setValue(partnerId);
        this.partnerIdPrefilled = true;
      }

      if (claimNumber !== null) {
        this.findTaskForm.controls['claimNumber'].setValue(claimNumber);
        this.claimNumberPrefilled = true;
      }
    });
  }

  get selectedPartnerName(): string {
    const currentId = this.findTaskForm.get('partnerId')?.value;
    if (!currentId || !this.insuranceCompanies) {
      return '';
    }
    return this.insuranceCompanies.find(c => c.id === currentId)?.name ?? '';
  }
}
